import { Box, Text, Group } from '@mantine/core'
import { DirectionArrow } from './_tiles'

// Date header per day: "Today" / "Tomorrow" (the payload starts at station-today,
// so index-based is SSR-safe) then "Monday - July 6, 2026". `locale` is the
// resolved UI locale (from useI18n) so the weekday/month names match the language
// on both server and client (using the browser default would render English on
// the SSR node and cause a hydration mismatch).
export function weekDate(t, locale, dateStr, index) {
  if (index === 0) return t('today')
  if (index === 1) return t('tomorrow')
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dateStr)
  try {
    const wd = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d)
    const rest = new Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(d)
    return `${wd} - ${rest}`
  } catch {
    return dateStr
  }
}

// Rounded day card holding the color bands (overflow hidden so bands meet the
// rounded corners cleanly).
export function WeekCard({ children }) {
  return <Box style={{ borderRadius: 12, overflow: 'hidden' }}>{children}</Box>
}

// A colored band (Tides / Sun / Moon / Currents): centered emoji + TITLE header,
// then rows. Tile color encodes the section, per the app. `badge` is an optional
// centered node under the header — the French convention shows the day's tidal
// coefficient once here (not per row), matching the app's week/month view.
export function WeekSection({ bg, emoji, title, badge, children }) {
  return (
    <Box style={{ background: bg, color: '#fff' }}>
      <Text
        ta="center"
        fw={700}
        tt="uppercase"
        fz="1.05rem"
        pt={10}
        pb={badge ? 4 : 10}
        style={{ letterSpacing: 0.5 }}
      >
        {emoji} {title}
      </Text>
      {badge && <Box style={{ textAlign: 'center', paddingBottom: 8 }}>{badge}</Box>}
      {children}
    </Box>
  )
}

// A row: left (arrow + label, or a type) | time (centered) | right (value / node).
//
// The Weather band fills all three cells with readings rather than a time —
// it has none to show, every daily reading being an aggregate over the whole
// day — which is why the middle slot takes nodes as well as strings. A node
// has to bypass the <Text> wrapper: Mantine's Text renders a <p>, so a
// <Group> inside it would nest a <div> in a <p>, which the parser auto-closes
// and SSR then mismatches on.
export function WeekRow({ left, time, right }) {
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 8,
        padding: '9px 16px',
        borderTop: '1px solid rgba(255,255,255,0.14)',
      }}
    >
      <div style={{ justifySelf: 'start' }}>{left}</div>
      {time != null && typeof time !== 'string' ? (
        <div style={{ justifySelf: 'center' }}>{time}</div>
      ) : (
        <Text fz="1.05rem" style={{ justifySelf: 'center', color: '#fff' }}>
          {time}
        </Text>
      )}
      {typeof right === 'string' ? (
        <Text fz="1.05rem" fw={600} style={{ justifySelf: 'end', color: '#fff' }}>
          {right}
        </Text>
      ) : (
        <div style={{ justifySelf: 'end' }}>{right}</div>
      )}
    </Box>
  )
}

// Gold up/down arrow + label ("High" / "Low" / "Rise" / "Set").
export function ArrowLabel({ up, label }) {
  return (
    <Group gap={6} wrap="nowrap">
      <DirectionArrow deg={up ? 0 : 180} size={16} />
      <Text fz="1.05rem" fw={600} style={{ color: '#fff' }}>
        {label}
      </Text>
    </Group>
  )
}
